/**
 * Renders a create-enquiry form when the AI returns type: "create_enquiry_form".
 * Single clean form with styling from CreateEventWizard (no wizard steps).
 */
import React, { useState, useEffect } from "react";
import { Card, Form, Button, Row, Col } from "react-bootstrap";
import PageLayout from "../../PageLayout";
import { fetchWithAuth } from "../../FetchWithAuth";
import { url } from "../../url";
import { getFormFieldId, mapApiFieldErrors } from "../../createEnquiryFieldErrors";
import styles from "../../../pages/CreateEnquiryWizard.module.scss";

function getInputType(name) {
  if (name === "email") return "email";
  if (name === "phone" || name === "mobile" || name === "telephone") return "tel";
  if (name === "date_of_birth") return "date";
  return "text";
}

export default function CreateEnquiryForm({ fields = [], onCreated, onError }) {
  const [values, setValues] = useState(() => {
    const v = {};
    fields.forEach((f) => {
      const id = getFormFieldId(f);
      if (id) v[id] = "";
    });
    return v;
  });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(""); 
  const [fieldErrors, setFieldErrors] = useState({});

  const applyServerValidation = (data, err) => {
    const raw = data.field_errors || {};
    const mapped = mapApiFieldErrors(raw, fields);
    setFieldErrors(mapped);
    const errMsg =
      data.error ||
      data.message ||
      (Object.keys(mapped).length ? Object.values(mapped).join(" ") : null) ||
      (Object.keys(raw).length ? Object.values(raw).join(" ") : null) ||
      (err && err.message) ||
      "Failed to create enquiry";
    setApiError(errMsg);
    if (typeof onError === "function") onError(errMsg);
  };

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      fields.forEach((f) => {
        const id = getFormFieldId(f);
        if (id && next[id] === undefined) {
          next[id] = "";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [fields]);

  useEffect(() => {
    if (!Object.keys(fieldErrors).length) return;
    const name =
      fields.map(getFormFieldId).find((id) => id && fieldErrors[id]) || Object.keys(fieldErrors)[0];
    const el = typeof document !== "undefined" ? document.getElementById(name) : null;
    if (el && typeof el.focus === "function") {
      el.focus();
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
  }, [fieldErrors, fields]);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setApiError("");
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setApiError("");
    setFieldErrors({});
    try {
      const combined = [values.first_name, values.last_name].filter(Boolean).join(" ") || values.name;
      const payload = {
        first_name: values.first_name || combined.split(" ")[0] || "",
        last_name: values.last_name || combined.split(" ").slice(1).join(" ") || "",
        email: values.email,
        mobile: String(values.phone || values.mobile || "").replace(/\D/g, ""),
        telephone: String(values.telephone || "").replace(/\D/g, ""),
        source: values.source || "Walk In",
        address: values.address || "",
        gender: values.gender || "Other",
        date_of_birth: values.date_of_birth || "",
        counsellor: values.counsellor || "",
      };
      const res = await fetchWithAuth(`${url}/enquiry/create_new_enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.id) {
        if (typeof onCreated === "function") onCreated(data);
      } else {
        applyServerValidation(data);
      }
    } catch (err) {
      const data = err.data || {};
      applyServerValidation(data, err);
    } finally {
      setLoading(false);
    }
  };

  // Pair fields for two-column layout where it makes sense
  const pairs = [
    ["first_name", "last_name"],
    ["email", "phone"],
    ["email", "mobile"],
  ];
  const used = new Set();
  const paired = [];
  for (const [a, b] of pairs) {
    if (used.has(a) || used.has(b)) continue;
    const fa = fields.find((x) => getFormFieldId(x) === a);
    const fb = fields.find((x) => getFormFieldId(x) === b);
    if (fa && fb) {
      used.add(a);
      used.add(b);
      paired.push([fa, fb]);
    }
  }
  const single = fields.filter((f) => !used.has(getFormFieldId(f)));

  return (
    <PageLayout
      title="Create enquiry"
      subtitle="Add a new lead from the chat."
    >
      <div className={styles.wizardWrap}>
        <Card className={styles.wizardCard}>
          <Card.Body className={styles.cardBody}>
            {apiError && (
            <div className="alert alert-danger py-2 mb-3" role="alert">
              {apiError}
            </div>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <section className={styles.stepBody}>
              {paired.map(([f1, f2]) => {
                const id1 = getFormFieldId(f1);
                const id2 = getFormFieldId(f2);
                return (
                  <Row key={`${id1}-${id2}`} className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Group controlId={id1}>
                        <Form.Label>
                          {f1.label || id1}
                          {f1.required && " *"}
                        </Form.Label>
                        <Form.Control
                          type={getInputType(id1)}
                          value={values[id1] ?? ""}
                          onChange={(e) => handleChange(id1, e.target.value)}
                          required={!!f1.required}
                          placeholder={f1.placeholder || ""}
                          isInvalid={Boolean(fieldErrors[id1])}
                        />
                        <Form.Control.Feedback type="invalid">{fieldErrors[id1]}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId={id2}>
                        <Form.Label>
                          {f2.label || id2}
                          {f2.required && " *"}
                        </Form.Label>
                        <Form.Control
                          type={getInputType(id2)}
                          value={values[id2] ?? ""}
                          onChange={(e) => handleChange(id2, e.target.value)}
                          required={!!f2.required}
                          placeholder={f2.placeholder || ""}
                          isInvalid={Boolean(fieldErrors[id2])}
                        />
                        <Form.Control.Feedback type="invalid">{fieldErrors[id2]}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })}
              {single.map((f) => {
                const fid = getFormFieldId(f);
                if (!fid) return null;
                return (
                  <Form.Group key={fid} controlId={fid} className="mb-3">
                    <Form.Label>
                      {f.label || fid}
                      {f.required && " *"}
                    </Form.Label>
                    <Form.Control
                      type={getInputType(fid)}
                      value={values[fid] ?? ""}
                      onChange={(e) => handleChange(fid, e.target.value)}
                      required={!!f.required}
                      placeholder={f.placeholder || ""}
                      isInvalid={Boolean(fieldErrors[fid])}
                    />
                    <Form.Control.Feedback type="invalid">{fieldErrors[fid]}</Form.Control.Feedback>
                  </Form.Group>
                );
              })}
            </section>

            <div className={styles.actions}>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? "Creating…" : "Create enquiry"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
    </PageLayout>
  );
}
